import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/core/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { isPayrollBusiness } from "@/core/business/payroll-only";
import { cn } from "@/core/utils/utils";
import { useGetMyUser } from "@/user/query";
import { isApOnly, isTech } from "@/user/utils";
import QuickbooksDropdownItem from "./quickbooks-dropdown-item";

interface ProfileDropdownProps {
  initials: string;
  isLimitedAccess?: boolean;
  showImpersonationButton: boolean | undefined;
  openImpersonationModal: () => void;
}

const PROFILE_ROUTES = {
  profile: "/profile",
  businessSettings: "/business-settings",
} as const;

const ProfileDropdown = ({
  initials,
  isLimitedAccess,
  showImpersonationButton,
  openImpersonationModal,
}: ProfileDropdownProps) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userInfo } = useGetMyUser();
  const { businessId, selectedBusiness } = useBusiness();

  const canSeeBusinessSettings =
    !isApOnly(userInfo?.role) &&
    !isTech(userInfo?.role) &&
    !isLimitedAccess &&
    !isPayrollBusiness(selectedBusiness);

  const isProfileSelected =
    location.pathname.includes(PROFILE_ROUTES.profile) ||
    location.pathname.includes(PROFILE_ROUTES.businessSettings);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label="Open profile menu"
        className="h-8 w-8 cursor-pointer rounded-full focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <Avatar className="h-8 w-8">
          <AvatarFallback
            className={cn(
              "text-md font-medium text-secondary-text transition-colors duration-300",
              isProfileSelected &&
                "border-2 border-stroke-tabSelected text-tab-selected-text",
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate(PROFILE_ROUTES.profile)}>
          My Profile
        </DropdownMenuItem>
        {canSeeBusinessSettings && (
          <DropdownMenuItem
            onClick={() => navigate(`/business-settings/${businessId}/`)}
          >
            Admin Settings
          </DropdownMenuItem>
        )}
        <QuickbooksDropdownItem />

        {showImpersonationButton && (
          <DropdownMenuItem onClick={openImpersonationModal}>
            Impersonate User
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
